import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const Counter = ({ to, suffix, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} data-testid={testid} className="font-display text-5xl font-semibold text-gold-deep sm:text-6xl">
      {val.toLocaleString()}
      {suffix}
    </span>
  );
};

const stats = [
  { to: 465, suffix: "+", label: "Families Assisted", testid: "stat-families" },
  { to: 1430, suffix: "+", label: "Mentorship Hours", testid: "stat-hours" },
  { to: 100, suffix: "%", label: "Community Driven", testid: "stat-community" },
];

const quotes = [
  {
    text: "They didn't just hand me diapers and formula — they handed me hope. I finally felt like someone in this town was on my side.",
    name: "A Madison County mother",
    testid: "testimonial-1",
  },
];

export default function Impact() {
  return (
    <section id="community" data-testid="impact-section" className="relative bg-cream py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-gold-deep" data-testid="impact-eyebrow">
            Chapter 04 — Community Impact
          </p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="impact-heading">
            Small town. <span className="italic text-gold-deep">Mighty love.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 border-y border-gold/25 py-12 sm:grid-cols-3">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.12} className="text-center sm:text-left">
              <Counter to={s.to} suffix={s.suffix} testid={s.testid} />
              <p className="mt-2 text-sm font-bold uppercase tracking-[0.18em] text-ink-soft">{s.label}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6">
          {quotes.map((q, i) => (
            <Reveal key={q.name} delay={i * 0.12}>
              <figure className="soft-shadow relative mx-auto h-full max-w-3xl rounded-[1.75rem] border border-gold/20 bg-white p-8 sm:p-10" data-testid={q.testid}>
                <Quote size={30} className="text-gold/60" fill="currentColor" strokeWidth={0} />
                <blockquote className="mt-5 font-display text-lg font-medium italic leading-relaxed text-ink sm:text-xl">
                  "{q.text}"
                </blockquote>
                <figcaption className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-gold-deep">
                  — {q.name}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
